import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  CreditCard,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Wallet,
  Clock,
  ChevronRight,
  FileText,
  CircleDollarSign
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import { apiEndpoints } from '../services/api';

const FinanceDashboard = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterType, setFilterType] = useState('month'); // 'day', 'month', 'year', 'all'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Paginated Transactions State
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);

  // Debts state
  const [debts, setDebts] = useState([]);
  const [debtsLoading, setDebtsLoading] = useState(false);

  const fetchDebts = useCallback(async () => {
    try {
      setDebtsLoading(true);
      const response = await apiEndpoints.debts.getAll('open');
      setDebts(response.data || []);
    } catch (error) {
      console.error('Error fetching debts:', error);
      setDebts([]);
    } finally {
      setDebtsLoading(false);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType === 'day') {
        const d = new Date(selectedDate + 'T12:00:00');
        params.year = d.getFullYear();
        params.month = d.getMonth() + 1;
        params.day = d.getDate();
      } else if (filterType === 'month') {
        params.year = selectedYear;
        params.month = selectedMonth;
      } else if (filterType === 'year') {
        params.year = selectedYear;
      }
      
      const response = await apiEndpoints.reports.getFinanceDashboard(params);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching finance dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [filterType, selectedYear, selectedMonth, selectedDate]);

  const fetchTransactions = useCallback(async (page = 1) => {
    try {
      setTransactionsLoading(true);
      const params = { page, pageSize };
      if (filterType === 'day') {
        const d = new Date(selectedDate + 'T12:00:00');
        params.year = d.getFullYear();
        params.month = d.getMonth() + 1;
        params.day = d.getDate();
      } else if (filterType === 'month') {
        params.year = selectedYear;
        params.month = selectedMonth;
      } else if (filterType === 'year') {
        params.year = selectedYear;
      }
      
      const response = await apiEndpoints.reports.getFinanceTransactions(params);
      setTransactions(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(response.data.page || 1);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  }, [filterType, selectedYear, selectedMonth, selectedDate, pageSize]);

  useEffect(() => {
    fetchDashboardData();
    fetchTransactions(1);
    fetchDebts();
  }, [fetchDashboardData, fetchTransactions, refreshKey, fetchDebts]);

  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#6366F1', '#EC4899'];
  const EXPENSE_COLORS = ['#EF4444', '#F43F5E', '#FB7185'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      maximumFractionDigits: 0
    }).format(value);
  };

  const isRTL = i18n.language === 'ar';

  if (loading && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-500 font-medium">{t('common.loading')}</p>
      </div>
    );
  }

  if (!data) return null;

  const incomeData = [
    { name: t('reports.riderPayments'), value: data.income.riderPayments },
    { name: t('reports.ownerPayments'), value: data.income.ownerPayments },
    { name: t('reports.temporaryPayments'), value: data.income.temporaryPayments },
    { name: t('reports.miscellaneous'), value: data.income.miscellaneousIncome }
  ].filter(item => item.value > 0);

  const expenseData = [
    { name: t('reports.personnelPayments') || 'Personnel', value: data.expenses.personnelPayments },
    { name: t('reports.institutionalPayments') || 'Institutional', value: data.expenses.institutionalPayments },
    { name: t('reports.otherPayments') || 'Other', value: data.expenses.otherPayments }
  ].filter(item => item && item.value > 0);

  const totalDebt = debts.reduce((sum, d) => sum + (d.remaining || 0), 0);

  return (
    <div className={`space-y-6 pb-20 ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('finance.dashboardTitle')}</h1>
          <p className="text-gray-500">{t('finance.dashboardSubtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex bg-white border border-gray-200 rounded-lg p-1 shadow-sm flex-wrap">
            <button
              onClick={() => setFilterType('day')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterType === 'day' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {t('finance.byDay')}
            </button>
            <button
              onClick={() => setFilterType('month')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterType === 'month' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {t('finance.byMonth')}
            </button>
            <button
              onClick={() => setFilterType('year')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterType === 'year' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {t('finance.year')}
            </button>
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterType === 'all' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {t('finance.allTime')}
            </button>
          </div>

          {filterType === 'day' && (
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          )}

          {filterType !== 'all' && filterType !== 'day' && (
            <div className="flex items-center gap-2">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
              >
                {[...Array(5)].map((_, i) => {
                  const y = new Date().getFullYear() - i;
                  return <option key={y} value={y}>{y}</option>;
                })}
              </select>

              {filterType === 'month' && (
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2000, i).toLocaleString(i18n.language, { month: 'long' })}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <button
            onClick={() => setRefreshKey(prev => prev + 1)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-all text-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{t('common.refresh')}</span>
          </button>
        </div>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title={t('finance.totalIncome')}
          value={data.totalIncome}
          icon={<ArrowUpRight className="text-emerald-500" />}
          bg="bg-emerald-50"
          color="text-emerald-700"
          isRTL={isRTL}
          formatCurrency={formatCurrency}
        />
        <KPICard
          title={t('finance.totalExpenses')}
          value={data.totalExpenses}
          icon={<ArrowDownRight className="text-rose-500" />}
          bg="bg-rose-50"
          color="text-rose-700"
          isRTL={isRTL}
          formatCurrency={formatCurrency}
        />
        <KPICard
          title={t('finance.netProfit')}
          value={data.netProfit}
          icon={<DollarSign className={`${data.netProfit >= 0 ? 'text-blue-500' : 'text-amber-500'}`} />}
          bg={data.netProfit >= 0 ? 'bg-blue-50' : 'bg-amber-50'}
          color={data.netProfit >= 0 ? 'text-blue-700' : 'text-amber-700'}
          isRTL={isRTL}
          formatCurrency={formatCurrency}
        />
        <KPICard
          title={t('finance.totalDebt')}
          value={totalDebt}
          icon={<CircleDollarSign className="text-rose-500" />}
          bg="bg-rose-50"
          color="text-rose-700"
          isRTL={isRTL}
          formatCurrency={formatCurrency}
        />
      </div>

      {/* Debts Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CircleDollarSign className="w-5 h-5 text-rose-500" />
            <h3 className="text-lg font-bold text-gray-800">{t('finance.debtsSection')}</h3>
          </div>
          {debtsLoading && <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left rtl:text-right text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th className="px-6 py-3">{t('finance.person')}</th>
                <th className="px-6 py-3">{t('finance.role')}</th>
                <th className="px-6 py-3">{t('finance.label')}</th>
                <th className="px-6 py-3">{t('finance.totalOwed')}</th>
                <th className="px-6 py-3">{t('finance.paid')}</th>
                <th className="px-6 py-3">{t('finance.remaining')}</th>
                <th className="px-6 py-3">{t('finance.date')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {debts.length > 0 ? (
                debts.map((d, idx) => (
                  <tr key={idx} className="bg-white hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">{d.memberName}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${d.memberRole === 'coach' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {d.memberRole === 'coach' ? t('reports.coach') : t('reports.rider')}
                      </span>
                    </td>
                    <td className="px-6 py-4">{d.label}</td>
                    <td className="px-6 py-4">{formatCurrency(d.amountOwed)}</td>
                    <td className="px-6 py-4 text-emerald-600">{formatCurrency(d.amountPaid)}</td>
                    <td className="px-6 py-4 font-bold text-rose-600">{formatCurrency(d.remaining)}</td>
                    <td className="px-6 py-4">
                      {new Date(d.createdAt).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-400 italic">
                    {t('common.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Breakdown Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income Breakdown */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">{t('finance.incomeBreakdown')}</h3>
            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full">
              {formatCurrency(data.income.total)}
            </span>
          </div>
          <div className="flex flex-col md:flex-row items-center">
            <div className="w-full md:w-1/2 h-[200px] min-h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                {incomeData.length > 0 ? (
                  <PieChart>
                    <Pie
                      data={incomeData}
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {incomeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm italic">
                    {t('common.noData')}
                  </div>
                )}
              </ResponsiveContainer>
            </div>
            <div className="w-full md:w-1/2 space-y-3 mt-4 md:mt-0">
              {incomeData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                    <span>{item.name}</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-bold text-gray-800">{formatCurrency(item.value)}</span>
                    <span className="text-xs text-gray-400">{((item.value / (data.income.total || 1)) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Expense Breakdown */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">{t('finance.expenseBreakdown')}</h3>
            <span className="px-3 py-1 bg-rose-100 text-rose-700 text-sm font-medium rounded-full">
              {formatCurrency(data.expenses.total)}
            </span>
          </div>
          <div className="flex flex-col md:flex-row items-center">
            <div className="w-full md:w-1/2 h-[200px] min-h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                {expenseData.length > 0 ? (
                  <PieChart>
                    <Pie
                      data={expenseData}
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {expenseData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm italic">
                    {t('common.noData')}
                  </div>
                )}
              </ResponsiveContainer>
            </div>
            <div className="w-full md:w-1/2 space-y-3 mt-4 md:mt-0">
              {expenseData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: EXPENSE_COLORS[index % EXPENSE_COLORS.length] }}></span>
                    <span>{item.name}</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-bold text-gray-800">{formatCurrency(item.value)}</span>
                    <span className="text-xs text-gray-400">{((item.value / (data.expenses.total || 1)) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Trend Chart */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-bold text-gray-800 mb-6">
          {filterType === 'day' ? t('finance.dailyTrend') : t('finance.monthlyTrend')}
        </h3>
        <div className="h-[300px] w-full min-h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.monthlyTrends}>
              <defs>
                <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="monthName" axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} tickFormatter={(val) => val >= 1000 ? `${val/1000}k` : val} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend verticalAlign="top" height={36} />
              <Area type="monotone" dataKey="income" name={t('finance.income')} stroke="#10B981" strokeWidth={3} fillOpacity={1} fill="url(#colorIncome)" />
              <Area type="monotone" dataKey="expenses" name={t('finance.expenses')} stroke="#EF4444" strokeWidth={3} fillOpacity={1} fill="url(#colorExpenses)" />
              <Area type="monotone" dataKey="profit" name={t('finance.profit')} stroke="#3B82F6" strokeWidth={2} strokeDasharray="5 5" fill="transparent" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Contributors */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-50 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800">{t('finance.topContributors')}</h3>
            <Users className="w-5 h-5 text-gray-400" />
          </div>
          <div className="divide-y divide-gray-50">
            {data.topContributors.length > 0 ? (
              data.topContributors.map((c, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                      {c.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{c.name}</p>
                      <p className="text-xs text-gray-500">{c.type === 'Rider' ? t('reports.rider') : t('reports.owner')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-600">{formatCurrency(c.amount)}</p>
                    <p className="text-xs text-gray-400">{c.paymentCount} {t('finance.payments')}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-400">{t('common.noData')}</div>
            )}
          </div>
        </div>

        {/* Pending Payments - REMOVED */}
      </div>

      {/* Paginated Transactions Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-50 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-bold text-gray-800">{t('finance.recentTransactions')}</h3>
          </div>
          {transactionsLoading && <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />}
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left rtl:text-right text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50/50">
              <tr>
                <th className="px-6 py-4">{t('finance.date')}</th>
                <th className="px-6 py-4">{t('finance.person')}</th>
                <th className="px-6 py-4">{t('finance.category')}</th>
                <th className="px-6 py-4">{t('finance.amount')}</th>
                <th className="px-6 py-4">{t('finance.type')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.length > 0 ? (
                transactions.map((transaction, idx) => (
                  <tr key={idx} className="bg-white hover:bg-gray-50/80 transition-colors">
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(transaction.date).toLocaleDateString(i18n.language, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{transaction.person}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        {transaction.category}
                      </span>
                    </td>
                    <td className={`px-6 py-4 font-bold ${transaction.type === 'In' ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {transaction.type === 'In' ? '+' : '-'}{formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        transaction.type === 'In' 
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                          : 'bg-rose-50 text-rose-700 border border-rose-100'
                      }`}>
                        {transaction.type === 'In' ? t('finance.deposit') : t('finance.withdrawal')}
                        {transaction.type === 'In' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-400 italic">
                    {t('common.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="p-4 bg-gray-50/30 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500 uppercase font-medium tracking-wider">
              {t('common.page')} {currentPage} {t('common.of')} {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => fetchTransactions(currentPage - 1)}
                disabled={currentPage === 1 || transactionsLoading}
                className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {t('common.previous')}
              </button>
              <button
                onClick={() => fetchTransactions(currentPage + 1)}
                disabled={currentPage === totalPages || transactionsLoading}
                className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Personnel Costs Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800">{t('finance.personnelCosts')}</h3>
          <Users className="w-5 h-5 text-gray-400" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left rtl:text-right text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th className="px-6 py-3">{t('finance.employeeName')}</th>
                <th className="px-6 py-3">{t('finance.monthlySalary')}</th>
                <th className="px-6 py-3">{t('finance.paymentStatus')}</th>
                <th className="px-6 py-3">{t('finance.lastPayment')}</th>
                <th className="px-6 py-3">{t('finance.totalPaidYear')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(data.personnelCosts || []).map((p, idx) => (
                <tr key={idx} className="bg-white hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">{p.name}</td>
                  <td className="px-6 py-4 font-bold text-gray-800">{formatCurrency(p.salary)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${p.paymentStatus === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                      {p.paymentStatus === 'Paid' ? t('common.paid') : t('common.pending')}
                    </span>
                  </td>
                  <td className="px-6 py-4">{p.lastPaymentDate ? new Date(p.lastPaymentDate).toLocaleDateString() : '-'}</td>
                  <td className="px-6 py-4 font-semibold text-gray-700">{formatCurrency(p.totalPaid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const KPICard = ({ title, value, icon, bg, color, isRTL, formatCurrency }) => (
  <div className={`bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between hover:shadow-md transition-shadow`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-gray-500">{title}</span>
      <div className={`p-2 rounded-xl ${bg}`}>
        {icon}
      </div>
    </div>
    <div className={`text-xl font-bold ${color}`}>
      {formatCurrency(value)}
    </div>
  </div>
);

export default FinanceDashboard;
